import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../services/supabase';
import { useLovense } from '../../hooks/useLovense';
import { SafeWordButton } from '../SafeWordButton';
import toast from 'react-hot-toast';

export const SyncChallenge = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const role = (searchParams.get('role') || 'A') as 'A' | 'B';
  
  const { vibrate } = useLovense();
  
  const [syncScore, setSyncScore] = useState(0);
  const [lastSync, setLastSync] = useState<{ diff: number; success: boolean } | null>(null);
  const [pulseActive, setPulseActive] = useState(false);
  const channelRef = useRef<any>(null);

  useEffect(() => {
    const channel = supabase.channel(`game-${sessionId}`);
    channelRef.current = channel;
    
    channel
      .on('broadcast', { event: 'game_action' }, ({ payload }: any) => {
        const { action, response } = payload;
        if (action.action === 'sync_attempt') {
          setLastSync({ diff: response.diff, success: response.synced });
          if (response.synced) {
            setSyncScore(response.score);
            vibrate(15);
            toast.success('Perfect sync! ⚡', { duration: 1000 });
          }
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, vibrate]);

  const sendPulse = useCallback(() => {
    setPulseActive(true);
    setTimeout(() => setPulseActive(false), 100);
    
    channelRef.current?.send({
      type: 'broadcast',
      event: 'game_action',
      payload: { action: { playerId: role, action: 'sync_attempt' }, response: { timestamp: Date.now() } }
    });
  }, [sessionId, role]);

  return (
    <div className="game-container">
      <div style={{ textAlign: 'center' }}>
        <h1>Sync Challenge</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Press the button at the exact same time as your partner!
        </p>
      </div>

      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '5rem', fontWeight: 'bold', color: 'var(--primary)' }}>
          {syncScore}
        </div>
        <div style={{ color: 'var(--text-muted)' }}>Sync Score</div>
      </div>

      {lastSync && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            textAlign: 'center',
            padding: '1rem 2rem',
            borderRadius: 'var(--border-radius)',
            background: lastSync.success ? 'var(--success)' : 'var(--bg-tertiary)',
            color: lastSync.success ? 'white' : 'var(--text-primary)'
          }}
        >
          {lastSync.success ? (
            <>⚡ Perfect! {lastSync.diff}ms difference</>
          ) : (
            <>❌ Missed by {lastSync.diff}ms</>
          )}
        </motion.div>
      )}

      <motion.button
        className="btn btn-primary btn-lg"
        onMouseDown={sendPulse}
        onTouchStart={sendPulse}
        animate={pulseActive ? { scale: 0.95 } : { scale: 1 }}
        style={{
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          fontSize: '1.5rem',
          boxShadow: pulseActive ? '0 0 50px var(--primary)' : '0 4px 20px rgba(108, 92, 231, 0.4)'
        }}
      >
        SYNC!
      </motion.button>

      <div style={{ color: 'var(--text-muted)', textAlign: 'center', maxWidth: '400px' }}>
        Both you and your partner should press this button at the exact same moment. 
        The closer your timing, the higher your sync score!
      </div>

      <SafeWordButton sessionId={sessionId!} />
    </div>
  );
};